import React from "react";
import { Card, Chip, Typography } from "@/components/ui";
import { useChartColors } from "@/hooks/use-chart-colors";
import { statusColors } from "@/styles/colors";
import type { ViewSize } from "@/components/layout/gallery-layout/types";

/**
 * Configuration d'une colonne du Board
 */
export interface BoardColumn<T> {
  id: string;
  title: string;
  items: T[];
  color: string;
  icon?: React.ReactNode;
}

/**
 * Props du composant BoardView générique
 */
export interface BoardViewProps<T extends { id: string }> {
  /** Colonnes à afficher */
  columns: BoardColumn<T>[];
  /** Fonction de rendu d'un élément */
  renderItem: (item: T) => React.ReactNode;
  /** Message affiché quand une colonne est vide */
  emptyMessage?: string;
  /** View size for column width (S/M/L) */
  viewSize?: ViewSize;
}

const COLUMN_WIDTH: Record<ViewSize, string> = {
  small: "260px",
  medium: "320px",
  large: "420px",
};

const COL_GAP: Record<ViewSize, string> = {
  small: "gap-2",
  medium: "gap-4",
  large: "gap-5",
};

const HEADER_PAD: Record<ViewSize, string> = {
  small: "p-2.5",
  medium: "p-4",
  large: "p-5",
};

const HEADER_TITLE: Record<ViewSize, string> = {
  small: "text-xs",
  medium: "text-sm",
  large: "text-base",
};

const ITEM_GAP: Record<ViewSize, string> = {
  small: "gap-1.5",
  medium: "gap-3",
  large: "gap-4",
};

/**
 * Composant BoardView générique pour afficher des éléments en colonnes (style Kanban)
 * Utilisé pour les trackers et les enrichissements
 */
function BoardView<T extends { id: string }>({
  columns,
  renderItem,
  emptyMessage = "Aucun élément dans ce groupe",
  viewSize = "medium",
}: BoardViewProps<T>) {
  const colWidth = COLUMN_WIDTH[viewSize];

  return (
    <div className={`flex ${COL_GAP[viewSize]} overflow-x-auto pb-4 h-full`}>
      {columns.map((column) => {
        return (
          <div
            key={column.id}
            className="flex-shrink-0 flex flex-col h-full"
            style={{ width: colWidth, minWidth: colWidth }}
          >
            <Card
              className={`${HEADER_PAD[viewSize]} mb-3 rounded-arc-lg flex-shrink-0`}
              style={{ backgroundColor: column.color + "15" }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1">
                  {column.icon}
                  <Typography variant="h6" className={`${HEADER_TITLE[viewSize]} font-semibold`}>
                    {column.title}
                  </Typography>
                </div>
                <Chip
                  label={String(column.items.length)}
                  size="sm"
                  style={{
                    backgroundColor: column.color,
                    color: "white",
                  }}
                />
              </div>
            </Card>
            <div className={`flex-1 min-h-0 flex flex-col ${ITEM_GAP[viewSize]} overflow-y-auto custom-scrollbar`}>
              {column.items.map((item) => (
                <React.Fragment key={item.id}>
                  {renderItem(item)}
                </React.Fragment>
              ))}
              {column.items.length === 0 && (
                <Card className="p-8 text-center border-2 border-dashed border-arc-border rounded-arc-lg bg-transparent">
                  <Typography variant="text-sm" className="text-content-tertiary">
                    {emptyMessage}
                  </Typography>
                </Card>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Hook utilitaire pour générer les couleurs des colonnes
 */
export function useBoardColumnColors() {
  const chartColors = useChartColors();

  const getStatusColor = (status: string): string => {
    return status === "Actifs" ? statusColors.active : statusColors.inactive;
  };

  const getColorByIndex = (key: string): string => {
    const index = key.charCodeAt(0) % chartColors.vibrant.length;
    return chartColors.vibrant[index];
  };

  return { getStatusColor, getColorByIndex, chartColors };
}

export default BoardView;
